import {
  DocumentReference,
  isDocRef,
  isObject,
  ISoftDelete,
  Region,
  TypeGuard,
} from '@principle-theorem/shared';
import { isBoolean, isString } from 'lodash';
import { SmsIntegrationOption } from '../interaction/sms-integration';
import { IStaffer } from '../staffer/staffer';

export enum OrganisationStatus {
  Active = 'active',
  Pending = 'pending',
  Cancelled = 'cancelled',
  Suspended = 'suspended',
  Provisioning = 'provisioning',
}

export interface IOrganisationIntegrationOptions {
  smsProvider: SmsIntegrationOption;
}

export interface IOrganisationSettings {
  multifactor?: {
    enabled: boolean;
  };
}

export interface IOrganisation extends ISoftDelete {
  name: string;
  slug: string;
  status: OrganisationStatus;
  region: Region;
  integrations?: Partial<IOrganisationIntegrationOptions>;
  settings?: IOrganisationSettings;
  optInFeatures?: string[];
  bigqueryDataset?: string;
  accountManagers?: DocumentReference<IStaffer>[];
}

function isOrganisationMultifactorSettings(
  data: unknown
): data is IOrganisationSettings['multifactor'] {
  return TypeGuard.interface<Required<IOrganisationSettings>['multifactor']>({
    enabled: isBoolean,
  })(data);
}

export function isOrganisationSettings(
  data: unknown
): data is IOrganisationSettings {
  return TypeGuard.interface<IOrganisationSettings>({
    multifactor: TypeGuard.nilOr(isOrganisationMultifactorSettings),
  })(data);
}

export const isOrganisation = TypeGuard.interface<IOrganisation>({
  name: isString,
  slug: isString,
  status: TypeGuard.enumValue(OrganisationStatus),
  region: TypeGuard.enumValue(Region),
  integrations: TypeGuard.nilOr(isObject),
  settings: TypeGuard.nilOr(isOrganisationSettings),
  optInFeatures: TypeGuard.nilOr(TypeGuard.arrayOf(isString)),
  deleted: isBoolean,
  bigqueryDataset: TypeGuard.nilOr(isString),
  accountManagers: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
});

export const ORGANISATION_STATUS_MAP: {
  [key in OrganisationStatus]: string;
} = {
  [OrganisationStatus.Pending]: 'default',
  [OrganisationStatus.Provisioning]: 'default',
  [OrganisationStatus.Active]: 'primary',
  [OrganisationStatus.Cancelled]: 'default',
  [OrganisationStatus.Suspended]: 'warn',
};

export enum OrganisationCollection {
  Users = 'users',
  Brands = 'brands',
  FeeSchedules = 'feeSchedules',
  Roles = 'roles',
  Integrations = 'integrations',
  TemporaryTokens = 'temporaryTokens',
  Subscriptions = 'subscriptions',
  ApiKeys = 'apiKeys',
  Webhooks = 'webhooks',
}

export enum CollectionGroup {
  CalendarEvents = 'calendarEvents',
  Appointments = 'appointments',
  TreatmentPlans = 'treatmentPlans',
  Templates = 'templates',
  LabJobs = 'labJobs',
  TemporaryTokens = 'temporaryTokens',
  Invoices = 'invoices',
  Transactions = 'transactions',
  FeeSchedules = 'feeSchedules',
  ConditionConfigurations = 'conditionConfigurations',
  TreatmentConfigurations = 'treatmentConfigurations',
  MultiTreatmentConfigurations = 'multiTreatmentConfigurations',
  BridgeDevices = 'bridgeDevices',
  AutomationConfigurations = 'automationConfigurations',
  AccountCredits = 'credits',
  CallEvents = 'callEvents',
  ReportRuns = 'reportRuns',
  DepositRequests = 'depositRequests',
}
